import { createRouter, createWebHistory } from 'vue-router'

import ability from '../services/ability'

import { adminRoute } from './modules/adminRoute'
import { advisorRoute } from './modules/advisorRoute'
import { apRouteAdmin } from './modules/apRouteAdmin'
import { apRouteCeo } from './modules/apRouteCeo'
import { ceoRoute } from './modules/ceoRoute'
import { clientPreviewRoute } from './modules/clientPreviewRoute'
import { clientRoute } from './modules/clientRoute'
import { leadRoute } from './modules/leadRoute'
import { leadSecureUploadRoute } from './modules/leadSecureUploadRoute'
import { loginRoute } from './modules/loginRoute'
import { magicOTPRoute } from './modules/magicOTPRoute'
import { otherRoute } from './modules/otherRoute'
import { secureOTPRoute } from './modules/secureOTPRoute'
import { secureUploadOTPRoute } from './modules/secureUploadOTPRoute'
import { settingsRoute } from './modules/settingsRoute'
import { supportRoute } from './modules/supportRoute'

import { useRoutRedirect } from '@/hooks/use-rout-redirect'
import { useSetInit } from '@/hooks/use-set-init'
import { useSetUpdateAbility } from '@/hooks/use-set-update-ability'
import Home from '@/layouts/Home.vue'
import { handleSecureUploadRedirect } from '@/router/modules/secureUploadRedirect'
import store from '@/store'

const routes = [
  clientRoute,
  leadRoute,
  adminRoute,
  ceoRoute,
  advisorRoute,
  apRouteAdmin,
  apRouteCeo,
  supportRoute,
  otherRoute,
  settingsRoute,
  loginRoute,
  clientPreviewRoute,
  magicOTPRoute,
  secureOTPRoute,
  secureUploadOTPRoute,
  leadSecureUploadRoute,
  {
    path: '/telegram-login',
    name: 'telegram-login',
    component: () => import(/* webpackChunkName: "Forbidden" */ '../views/TelegramLogin.vue'),
  },
  {
    path: '/403',
    name: '403',
    component: () => import(/* webpackChunkName: "Forbidden" */ '../views/Forbidden.vue'),
  },

  {
    path: '/404',
    name: '404',
    component: () => import(/* webpackChunkName: "PageNotFound" */ '../views/PageNotFound.vue'),
  },

  {
    path: '/no-permissions',
    name: 'no-permissions',
    component: () => import(/* webpackChunkName: "NoPermissions" */ '../views/NoPermissions.vue'),
  },

  {
    path: '/logout',
    name: 'logout',
    component: () => import(/* webpackChunkName: "Logout" */ '../views/Logout.vue'),
  },

  {
    path: '/terms',
    name: 'terms',
    component: () => import(/* webpackChunkName: "TermsConditions" */ '../views/TermsConditions.vue'),
  },
  {
    path: '/choose-account',
    name: 'choose_account',
    component: () => import(/* webpackChunkName: "ChooseAccount" */ '../views/ChooseAccount.vue'),
  },
  {
    path: '/',
    name: 'home',
    component: Home,
  },
  // Catch-all route to redirect to 404 page
  {
    path: '/:catchAll(.*)',
    name: 'default-page',
    component: () => import(/* webpackChunkName: "PageNotFound" */ '../views/PageNotFound.vue'),
  },
]

function getCanNavigate(item) {
  const rrr = Object.entries(item)
  return ability.can(...rrr[0])
}

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const redirect = handleSecureUploadRedirect(to)
  if (redirect) {
    return redirect
  }

  // Allow navigation to logout route without any processing
  if (to.name === 'logout') {
    return true
  }

  // Always reset UI + auth state when going to login, maintenance, or forgotpassword
  if (to.name === 'loginform' || to.name === 'forgotpassword') {
    await store.dispatch('globalComponents/resetState')
    store.commit('auth/setAuthUser', false)
    localStorage.clear()

    return true
  }
  if (to?.name == 'default-page') {
    const { routRedirect } = useRoutRedirect()
    // const sessionStorageData = sessionStorage.getItem('role') // removed unused variable

    // Parse the JSON string into an object
    let globalComponents = {}
    if (localStorage.getItem('role')) {
      try {
        globalComponents = JSON.parse(localStorage.getItem('role'))
      } catch (e) {
        console.error('Invalid JSON in localStorage for key "role":', localStorage.getItem('role'), e)
      }
    }
    // Get the value of the 'role, userid' property
    const role = globalComponents.globalComponents?.role
    const userId = globalComponents.globalComponents?.userid

    if (localStorage.getItem('access_token') && role) {
      // For client role, ensure userId exists before redirecting
      if (role?.toLowerCase() === 'client' && (!userId || userId === 'undefined' || userId === 'null')) {
        return '/login'
      }
      routRedirect({ role, userId })
    }
  }
  if (to.meta.publicRoute) {
    return true
  } else {
    store.commit('globalComponents/setDestinationItemsPerPage', {
      destination: 'default',
      value: 10,
    })

    if (!localStorage.getItem('access_token')) {
      const redirectPath = to.fullPath
      sessionStorage.setItem('post_login_redirect', redirectPath)

      await store.dispatch('globalComponents/resetState')
      store.commit('auth/setAuthUser', false)
      localStorage.clear()

      return {
        path: '/login',
        query: { redirect: redirectPath },
      }
    }
  }

  const { setInit } = useSetInit()
  const { setUpdateAbility } = useSetUpdateAbility()

  if (store.state.auth.isAuth && !store.state.globalComponents.currentCompanyId && !store.state.globalComponents.role) {
    await setInit(to)
  }
  if (!ability.rules.length) {
    await setUpdateAbility()
  }

  if (to.meta?.type) {
    store.commit('globalComponents/setCurrentTypeUser', to.meta.type)
  }

  const canNavigate = to.matched.some((route) => {
    if (route.meta.resource) {
      return route.meta.resource.some(getCanNavigate)
    }
  })

  store.commit('globalComponents/setSortMembers', {
    orderBy: '',
    sortedBy: '',
  })

  if (!canNavigate && to.meta.resource) {
    //return '/403'
  }

  store.commit('globalComponents/setSortMembers', {
    orderBy: '',
    sortedBy: '',
  })

  return true
})

export default router
